import * as React from 'react';
import { cn } from '@antimatterhq/ui/lib/utils';
import { Icon, IconType } from '../icon/icon';
import { Button } from '../button/button';

export interface SideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  side?: 'left' | 'right';
  isExpanded?: boolean;
  onExpandToggle?: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
  width?: 'default' | 'narrow' | 'wide' | 'full';
  showExpandButton?: boolean;
  showCloseButton?: boolean;
  backdrop?: boolean;
  closeOnEscape?: boolean;
  closeOnBackdropClick?: boolean;
  headerContent?: React.ReactNode;
  closeIcon?: IconType;
  expandIcon?: IconType;
  collapseIcon?: IconType;
  onAnimationEnd?: () => void;
  backDropClassName?: string;
}

const widthClasses = {
  default: 'w-3/4',
  narrow: 'w-1/3',
  half: 'w-1/2',
  wide: 'w-2/3',
  full: 'w-full',
};

export const SideDrawer = React.forwardRef<HTMLDivElement, SideDrawerProps>(
  (
    {
      isOpen,
      onClose,
      side = 'right',
      isExpanded = false,
      onExpandToggle,
      title,
      children,
      className,
      contentClassName,
      headerClassName,
      width = 'default',
      showExpandButton = true,
      showCloseButton = true,
      backdrop = true,
      closeOnEscape = true,
      closeOnBackdropClick = true,
      headerContent,
      closeIcon = 'lucide:chevrons-right',
      expandIcon = 'lucide:expand',
      collapseIcon = 'lucide:chevron-left',
      onAnimationEnd,
      backDropClassName,
    },
    ref,
  ) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [isMounted, setIsMounted] = React.useState(false);

    // Handle mounting and visibility animation
    React.useEffect(() => {
      let animationTimer: NodeJS.Timeout;

      if (isOpen && !isMounted) {
        // Opening: mount first, then animate in
        setIsMounted(true);
        // Use double RAF to ensure paint has happened
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsVisible(true);
          });
        });
      } else if (!isOpen && isMounted) {
        // Closing: animate out first, then unmount
        setIsVisible(false);
        animationTimer = setTimeout(() => {
          setIsMounted(false);
          onAnimationEnd?.();
        }, 300); // Match CSS transition duration
      }

      return () => {
        if (animationTimer) clearTimeout(animationTimer);
      };
    }, [isOpen, isMounted, onAnimationEnd]);

    // Handle escape key
    React.useEffect(() => {
      if (!isOpen || !closeOnEscape) return;

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose, closeOnEscape]);

    // Don't render if not mounted
    if (!isMounted) return null;

    const translateClass = {
      left: isVisible ? 'translate-x-0' : '-translate-x-full',
      right: isVisible ? 'translate-x-0' : 'translate-x-full',
    };

    const positionClasses = {
      left: 'left-0 top-0 bottom-0 border-r',
      right: 'right-0 top-0 bottom-0 border-l',
    };

    const iconRotation = side === 'left' ? 'rotate-180' : '';

    return (
      <>
        {/* Backdrop */}
        {backdrop && (
          <div
            className={cn(
              'fixed inset-0 z-drawer !m-0 !p-0',
              backDropClassName,
            )}
          >
            <div
              className={cn(
                'absolute inset-0 m-0 bg-black/50 p-0 transition-opacity duration-300 ease-out',
                isVisible ? 'opacity-100' : 'opacity-0',
              )}
              onClick={closeOnBackdropClick ? onClose : undefined}
            />
          </div>
        )}

        {/* Drawer */}
        <div
          ref={ref}
          className={cn(
            'fixed flex flex-col shadow-xl',
            'z-drawer border-gray-200 bg-white dark:border-gray-800 dark:bg-black',
            'transform transition-all duration-300 ease-out',
            positionClasses[side],
            translateClass[side],
            isExpanded ? 'w-full' : widthClasses[width],
            className,
          )}
        >
          {/* Header */}
          {(title || showCloseButton || showExpandButton || headerContent) && (
            <div
              className={cn(
                'flex-shrink-0 border-b border-gray-200 px-4 py-3 dark:border-gray-800',
                headerClassName,
              )}
            >
              <div className="flex justify-between">
                <div className="flex flex-col gap-2">
                  {isExpanded ? (
                    showExpandButton && (
                      <Button
                        variant="link"
                        leftIcon={collapseIcon}
                        className="justify-start px-0 text-sm"
                        onClick={onClose}
                      >
                        Back
                      </Button>
                    )
                  ) : (
                    <div className="flex items-center gap-1">
                      {showCloseButton && (
                        <button
                          className="hover:bg-muted rounded p-1.5"
                          onClick={onClose}
                          title="Close"
                          aria-label="Close drawer"
                        >
                          <Icon
                            icon={closeIcon}
                            className={cn(
                              'h-4 w-4 text-gray-600 dark:text-gray-400',
                              iconRotation,
                            )}
                          />
                        </button>
                      )}
                      {showExpandButton && onExpandToggle && (
                        <button
                          className="hover:bg-muted rounded p-1.5"
                          onClick={onExpandToggle}
                          title="Expand"
                          aria-label="Expand drawer"
                        >
                          <Icon
                            icon={expandIcon}
                            className="h-4 w-4 text-gray-600 dark:text-gray-400"
                          />
                        </button>
                      )}
                    </div>
                  )}
                  {title && (
                    <span className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {title}
                    </span>
                  )}
                </div>
                {headerContent && (
                  <div className="flex items-center">{headerContent}</div>
                )}
              </div>
            </div>
          )}

          {/* Content */}
          <div
            className={cn('relative flex-1 overflow-hidden', contentClassName)}
          >
            {children}
          </div>
        </div>
      </>
    );
  },
);

SideDrawer.displayName = 'SideDrawer';
